import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import { Button } from "../../components/ui/button";
import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { formatDateTime } from "../../lib/format";
import { FilterBar, FilterField, PAGE_SIZE_OPTIONS, PagePager, inputCls, labelCls, orDash } from "../forex/parts";
import { Drawer } from "../forex-admin/parts";
import { accountLabel, post, printReceipt, useLedger, useLedgerCan, useLookups } from "./api";

const EMPTY = { fiscalYear: "", receiptType: "", startDate: "", endDate: "", account: "", pageSize: "50" };

const columns = (base) => [
  { label: "#" },
  { label: "Date" },
  { label: "Type" },
  { label: "Source" },
  { label: "Description" },
  { label: "Cost center" },
  { label: "Lines", align: "right" },
  { label: `Total (${base})`, align: "right" },
  { label: "Version", align: "right" },
  { label: "Created by" },
  { label: "", sticky: true },
];

const LINE_COLUMNS = ["#", "Account", "Description", "Currency", "Rate", "Amount", "Debit", "Credit"];

const print = (id) => printReceipt(id).catch((e) => toast.error(e.message));

function Field({ label, children }) {
  return (
    <div>
      <div className={labelCls}>{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

// One receipt with its lines and its edit/delete history (only the detail
// route sends `changes`).
function ReceiptView({ id, lk }) {
  const q = useLedger(`/receipts/${id}`, undefined, { placeholderData: undefined });
  if (q.error) return <ErrorBanner error={q.error} onRetry={() => q.refetch()} />;
  const r = q.data;
  if (!r) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Number">{r.number}</Field>
        <Field label="Date">{formatDateTime(r.date)}</Field>
        <Field label="Type">{r.receiptType}</Field>
        <Field label="Source">{r.source}</Field>
        <Field label="Fiscal year">{lk.years.find((y) => y._id === r.fiscalYear)?.title ?? r.fiscalYear}</Field>
        <Field label="Cost center">{orDash(lk.costCenter[r.costCenter]?.name)}</Field>
        <Field label="Total debit">{r.totals.debit} {lk.baseCurrency}</Field>
        <Field label="Total credit">{r.totals.credit} {lk.baseCurrency}</Field>
        <Field label="Version">{r.version}</Field>
        <Field label="Request id">{orDash(r.sourceKey)}</Field>
        <Field label="Created">
          {formatDateTime(r.createdAt)} · {r.createdBy}
        </Field>
        <Field label="Updated">
          {formatDateTime(r.updatedAt)} · {r.updatedBy}
        </Field>
      </div>
      <Field label="Description">{orDash(r.description)}</Field>
      <div className="overflow-x-auto rounded-md border border-border/70">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground">
              {LINE_COLUMNS.map((c) => (
                <th key={c} className="px-2 py-2 font-medium">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {r.lines.map((l, i) => (
              <tr key={i} className="border-t border-border/40">
                <td className="px-2 py-1.5">{i + 1}</td>
                <td className="px-2 py-1.5">
                  {l.accountCode} · {l.accountName}
                </td>
                <td className="px-2 py-1.5">{orDash(l.description)}</td>
                <td className="px-2 py-1.5">{l.currencyName || l.currency}</td>
                <td className="px-2 py-1.5 font-mono">{l.rate}</td>
                <td className="px-2 py-1.5 font-mono">{l.amount}</td>
                <td className="px-2 py-1.5 font-mono">{l.debit}</td>
                <td className="px-2 py-1.5 font-mono">{l.credit}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <div className={labelCls}>History</div>
        {r.changes?.length ? (
          <ul className="space-y-1 text-xs">
            {r.changes.map((c, i) => (
              <li key={i}>
                <span className="font-medium">{c.action}</span> · {formatDateTime(c.at)} · {c.by}
                {c.reason ? ` · ${c.reason}` : ""}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-muted-foreground">Never edited.</p>
        )}
      </div>
    </>
  );
}

// Soft delete: the receipt leaves every list and report but keeps its history.
function DeleteForm({ receipt, base, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await post(`/receipts/${receipt._id}/delete`, { version: receipt.version, reason });
      toast.success(`Receipt ${receipt.number} deleted`);
      onDone();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Receipt {receipt.number} ({receipt.totals.debit} {base}) will be removed from the ledger.
      </p>
      <div>
        <label className={labelCls}>Reason</label>
        <textarea required rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} />
      </div>
      <Button type="submit" variant="destructive" disabled={saving || !reason.trim()} className="w-full">
        {saving ? "Deleting…" : "Delete receipt"}
      </Button>
    </form>
  );
}

export default function TrustLedgerReceipts() {
  const lk = useLookups();
  const can = useLedgerCan();
  const qc = useQueryClient();
  const [draft, setDraft] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  // The backend needs a fiscal year; until one is picked, the open year.
  const fiscalYear = applied.fiscalYear || lk.openYear?._id;
  const q = useLedger(fiscalYear ? "/receipts" : null, { ...applied, fiscalYear, page });
  const yearOpen = (id) => lk.years.find((y) => y._id === id)?.status === "open";
  const canEdit = can("POST", "/receipts/:id/edit");
  const canDelete = can("POST", "/receipts/:id/delete");

  return (
    <QueueLayout
      eyebrow="Trust Ledger"
      title="Vouchers"
      accent="& receipts"
      description={`Every posted journal voucher. Totals are in ${lk.baseCurrency}; a deleted voucher leaves this list but keeps its history.`}
      isFetching={q.isFetching}
      onRefresh={() => q.refetch()}
    >
      {can("POST", "/receipts") && (
        <div>
          <Button asChild size="sm" variant="default">
            <Link to="/trust-ledger/receipts/new">
              <Plus className="h-3.5 w-3.5" /> New voucher
            </Link>
          </Button>
        </div>
      )}

      <FilterBar
        onApply={() => {
          setApplied(draft);
          setPage(1);
        }}
        onReset={() => {
          setDraft(EMPTY);
          setApplied(EMPTY);
          setPage(1);
        }}
      >
        <FilterField
          label="Fiscal year"
          name="fiscalYear"
          options={[
            { value: "", label: "Open year" },
            ...lk.years.map((y) => ({ value: y._id, label: `${y.title} (${y.status})` })),
          ]}
          draft={draft}
          setDraft={setDraft}
        />
        <FilterField
          label="Type"
          name="receiptType"
          options={[{ value: "", label: "All" }, ...(lk.options?.receiptTypes ?? []).map((t) => ({ value: t, label: t }))]}
          draft={draft}
          setDraft={setDraft}
        />
        <FilterField label="From date" name="startDate" type="date" draft={draft} setDraft={setDraft} />
        <FilterField label="To date" name="endDate" type="date" draft={draft} setDraft={setDraft} />
        <FilterField
          label="Account"
          name="account"
          options={[{ value: "", label: "All" }, ...lk.postable.map((a) => ({ value: a._id, label: accountLabel(a) }))]}
          draft={draft}
          setDraft={setDraft}
        />
        <FilterField label="Page size" name="pageSize" options={PAGE_SIZE_OPTIONS} draft={draft} setDraft={setDraft} />
      </FilterBar>

      {q.error ? (
        <ErrorBanner error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <>
          <QueueTable
            columns={columns(lk.baseCurrency)}
            isLoading={q.isLoading || !fiscalYear}
            rows={q.data?.data}
            emptyText="No vouchers match."
            renderRow={(r) => {
              const editable = r.source === "manual" && yearOpen(r.fiscalYear);
              return (
                <tr key={r._id} className="hover:bg-white/[0.02]">
                  <Td className="font-mono">{r.number}</Td>
                  <Td className="whitespace-nowrap font-mono text-muted-foreground">{formatDateTime(r.date)}</Td>
                  <Td className="whitespace-nowrap text-xs">{r.receiptType}</Td>
                  <Td className="text-xs">{r.source}</Td>
                  <Td className="max-w-xs text-xs">{orDash(r.description)}</Td>
                  <Td className="text-xs">{orDash(lk.costCenter[r.costCenter]?.name)}</Td>
                  <Td align="right" className="font-mono">
                    {r.lines.length}
                  </Td>
                  <Td align="right" className="font-mono">
                    {r.totals.debit}
                  </Td>
                  <Td align="right" className="font-mono">
                    {r.version}
                  </Td>
                  <Td className="text-xs">{r.createdBy}</Td>
                  <Td sticky className="whitespace-nowrap">
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => setViewing(r)}>
                        View
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => print(r._id)}>
                        Print
                      </Button>
                      {canEdit && editable && (
                        <Button asChild size="sm" variant="outline">
                          <Link to={`/trust-ledger/receipts/${r._id}/edit`}>Edit</Link>
                        </Button>
                      )}
                      {canDelete && editable && (
                        <Button size="sm" variant="outline" className="text-red-400" onClick={() => setDeleting(r)}>
                          Delete
                        </Button>
                      )}
                    </div>
                  </Td>
                </tr>
              );
            }}
          />
          <PagePager data={q.data} onChange={setPage} label="vouchers" />
        </>
      )}

      <Drawer open={viewing} onClose={() => setViewing(null)} title={viewing ? `Receipt ${viewing.number}` : ""}>
        {viewing && <ReceiptView id={viewing._id} lk={lk} />}
      </Drawer>
      <Drawer open={deleting} onClose={() => setDeleting(null)} title={deleting ? `Delete receipt ${deleting.number}` : ""}>
        {deleting && (
          <DeleteForm
            receipt={deleting}
            base={lk.baseCurrency}
            onDone={() => {
              qc.invalidateQueries({ queryKey: ["trust-ledger"] });
              setDeleting(null);
            }}
          />
        )}
      </Drawer>
    </QueueLayout>
  );
}
